import { styled } from 'styled-components';
import Flex from 'components/ui/Flex';
import Text from 'components/ui/Text';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ReturnOrderLine, ReturnSummary } from 'returnCenter-api';
import { getStatusTypeString } from 'utils/helpers';
import CssSphere from 'components/ui/CssSphere';
import { getStatusColor } from './OrderStatus';
import {
    faEllipsisVertical,
    faRectangleHistoryCirclePlus,
    faSpinner,
    faTrash,
} from '@fortawesome/pro-regular-svg-icons';
import IconButton from 'components/ui/IconButton';
import { useState } from 'react';
import ItemsList from 'components/ui/ItemsList';
import { getAvailableOrderLinesForOrder, selectAvailableOrderLinesLoading } from 'store/reducers/returnSlice';
import useAppDispatch from 'hooks/useAppDispatch';
import useAppSelector from 'hooks/useAppSelector';

type OrderItemCountDisplayProps = {
    title: string;
    countLabel: string;
    items: ReturnOrderLine[];
    returnSummaries?: ReturnSummary[];
    onRemove?: () => void;
    canRemoveOrder?: boolean;
    showActions?: boolean;
};

const OrderItemCountDisplay: React.FC<OrderItemCountDisplayProps> = ({
    title,
    countLabel,
    items,
    returnSummaries,
    onRemove,
    canRemoveOrder,
    showActions = true,
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const loadingOrderLines = useAppSelector(selectAvailableOrderLinesLoading);

    const [showActionsMenu, setShowActionsMenu] = useState<boolean>(false);

    const count = items.length;

    const handleNavigate = (returnId?: number, status?: number) => {
        if (!returnId) return;
        const statusKey = getStatusTypeString(status);
        if (!statusKey) return;

        navigate(`/returns/${statusKey}/${returnId}`, {
            state: {
                origin: statusKey,
                returnIdFromPath: returnId,
                returnStatus: status,
            },
        });
    };

    const actions = [
        ...(canRemoveOrder
            ? [
                  {
                      icon: faTrash,
                      label: t('deleteOrderFromReturn', 'Delete order from return.'),
                      onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                          onRemove?.();
                          setShowActionsMenu(false);
                      },
                  },
              ]
            : []),

        {
            icon: loadingOrderLines ? faSpinner : faRectangleHistoryCirclePlus,
            label: t('loadOrderLines', 'Load available  lines'),
            onClick: () => {
                dispatch(getAvailableOrderLinesForOrder([items[0].orderNumber ?? '']));
            },
        },
    ];

    return (
        <Wrapper className="order-item-count-display-wrapper mt-3 mb-2" justify="between" align="center">
            <Flex align="center">
                <Heading as="h6" xBold fontSize={12} className="item-title">
                    {title}
                </Heading>
            </Flex>
            <Flex gap="1rem" align="center">
                {returnSummaries && returnSummaries.length > 0 && (
                    <PreviousReturnsWrapper align="center" gap="0.5rem" className="previous-returns-wrapper mb-1">
                        <Heading as="span" fontSize={12} className="previous-returns-label mb-1">
                            {t('returnsOnThisLine.column', 'Return history')}:
                        </Heading>
                        {returnSummaries.map((summary, idx) => (
                            <ReturnLinkWrapper
                                key={`${summary.returnId ?? 'return'}-${idx}`}
                                align="center"
                                gap="0.25rem"
                                className="return-link-wrapper"
                                onClick={() => handleNavigate(summary.returnId, summary.status)}
                            >
                                <CssSphere size={8} color={getStatusColor(summary.status ?? null)} />
                                <ReturnLink as="span" fontSize={11} bold>
                                    #{summary.returnId}
                                </ReturnLink>
                                <ReturnCount as="span" fontSize={11}>
                                    ({summary.totalReturnItems ?? 0})
                                </ReturnCount>
                            </ReturnLinkWrapper>
                        ))}
                    </PreviousReturnsWrapper>
                )}

                {showActions && (
                    <ActionsMenuContainer>
                        <RemoveButton
                            icon={faEllipsisVertical}
                            disableFont
                            bgColor="white"
                            onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();

                                setShowActionsMenu(true);
                            }}
                        />
                        {showActionsMenu && (
                            <Flex align="center" justify="center">
                                <ItemsList
                                    className="order-item-display-actions"
                                    absolutePositioned={{ left: '30px', bottom: '0px' }}
                                    onClickOutside={() => setShowActionsMenu(false)}
                                    onItemPress={(e, item) => {
                                        item.onClick(e);
                                    }}
                                    items={actions}
                                    childrenTitleKey="label"
                                    childrenIconKey="icon"
                                    breakParentWidth
                                />
                            </Flex>
                        )}
                    </ActionsMenuContainer>
                )}

                <Heading as="span" fontSize={12} className="item-count">
                    {t('result')}: {count} {countLabel.toLowerCase()}
                </Heading>
            </Flex>
        </Wrapper>
    );
};

export default OrderItemCountDisplay;

const Wrapper = styled(Flex)`
    border-bottom: 1px solid;
    margin-bottom: 0.5rem;
`;

const Heading = styled(Text)`
    margin: 5px;
    margin-bottom: 0.5rem;
`;

const PreviousReturnsWrapper = styled(Flex)`
    flex-wrap: wrap;
`;

const ReturnLinkWrapper = styled(Flex)`
    cursor: pointer;
    padding: 0.25rem 0.5rem;
    border-radius: var(--radius);
    background-color: ${({ theme }) => theme.colors.fillLight};
    &:hover {
        opacity: 0.8;
    }
`;

const ReturnLink = styled(Text)`
    color: ${({ theme }) => theme.colors.link};
`;

const ReturnCount = styled(Text)`
    color: ${({ theme }) => theme.colors.textFaded};
`;
const RemoveButton = styled(IconButton)`
    height: 24px;
    width: 25px;
    margin-bottom: 4px;
    border-radius: var(--radius);
    cursor: pointer;
    border: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const ActionsMenuContainer = styled(Flex)`
    position: relative;
`;
